<!-- 
  @component
  ### Dialog, fully customizable with mobile-sheet layout and buttons
  use this for more complex dialogs with lots of content and actions,
  but consider `<ConfirmDialog>` for simple confirmations, and `<ModalDialog>` for simple notifications
  eg: https://www.figma.com/design/n0GA6zpBrMsZTHXS66c9rG/DS02.-Components?node-id=1177-30228&t=riNaZx3ZkS6wOoW1-4
-->
<script lang="ts">
  import { BREAKPOINTS } from '$lib/constants';
  import { browser } from '$app/environment';
  import { createEventDispatcher, getContext } from 'svelte';
  import { uuidv4 } from '$lib/utils';
  import type { App } from '$lib/types';

  import Skeleton from '$components/mds/Dialogs/_Skeleton.svelte';
  import Grabber from './components/Grabber.svelte';
  import { DialogStatus, type DialogButtonAction } from '$components/mds/Dialogs/types';
  import { drawers as dialogs } from '$components/mds/Dialogs/stores';
  import { closeSheetPanel, scrollerSheetPanel } from '$lib/directives/sheetPanel';

  import Button from '$mds/Buttons/Button';
  import IconButton from '$mds/Buttons/IconButton/IconButton.svelte';
  import { faXmark, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
  import IconWrapper, { type IconComponentType } from '$mds/IconWrapper';

  export let isOpen = false;
  export let dialog: Skeleton | undefined = undefined;
  export let title: string = '';
  export let titleSize: 'xs' | 'sm' | 'md' | 'lg' = 'xs';
  export let subtitle: string = '';
  export let closeButton = true;
  export let closeButtonClass = '';
  export let backIcon = false;
  export let titleIcon: IconComponentType | null = null;
  export let primaryAction: DialogButtonAction | null = null;
  export let secondaryAction: DialogButtonAction | null = null;
  export let allowClickOutside: undefined | boolean = true;
  export let allowCloseOnNavigation: undefined | boolean = true;
  export let mobileSheet = true;
  export let mobileCloseButton = closeButton;
  export let focusOnOpen = false;
  export let closeOnActionClick = true;

  let className = '';
  export { className as class };
  export let dialogClass = '';
  export let style: string | null = null;
  export let scrollerClass = '-mx-2 p-2 pb-2 sm:pb-2';
  export let justifyButtons = 'justify-center';
  export let width = 'w-[680px]';
  export let fullHeightSheet = false;

  const dispatch = createEventDispatcher();
  const uuid = uuidv4();
  const { innerWidth } = getContext<App>('app') || {};

  const onOpenDelay = 500;
  const onCloseDelay = 500;

  export let dialogEl: HTMLDialogElement | undefined = undefined;
  let scrollerEl: HTMLDivElement;
  let status: DialogStatus = DialogStatus.Closed;
  let focusReady = focusOnOpen;

  $: isMobile = $innerWidth < BREAKPOINTS.sm;
  $: isSheetLayout = isMobile && mobileSheet;
  $: stackDepth = Math.max($dialogs.length - 1 - $dialogs.indexOf(uuid), 0);
  $: isTopOfStack = $dialogs[$dialogs.length - 1] === uuid;

  const titleSizeClasses = {
    xs: 'text-display-xs',
    sm: 'text-display-sm',
    md: 'text-display-md',
    lg: 'text-display-lg'
  };

  const dialogTransitionClasses: {
    [key in DialogStatus]: string;
  } = {
    open: 'scale-100 opacity-100 duration-500 ease-smooth-out',
    closing: 'scale-75 opacity-0 duration-250 ease-expo-in',
    closed: 'scale-75 opacity-0 duration-250 ease-expo-in'
  };

  const sheetTransitionClasses: {
    [key in DialogStatus]: string;
  } = {
    open: 'translate-y-0 duration-500 ease-smooth-out',
    closing: 'translate-y-full !duration-500 !opacity-0 ease-expo-in',
    closed: 'translate-y-full !duration-500 !opacity-0 ease-expo-in'
  };

  function addToStack() {
    if (dialog && isSheetLayout) {
      if ($dialogs.indexOf(uuid) < 0) {
        dialogs.update(dialogs => [...dialogs, uuid]);
      }
    }
  }

  function removeFromStack() {
    if (dialog && isSheetLayout) {
      dialogs.set([...$dialogs.filter(dialog => dialog !== uuid)]);
    }
  }

  $: {
    if (browser && allowClickOutside && isSheetLayout && dialogEl && dialog && isOpen) {
      closeSheetPanel(dialogEl, { onClose: dialog.closeModal });
    }
  }
</script>

<Skeleton
  bind:dialogEl
  bind:this={dialog}
  bind:isOpen
  bind:status
  id={uuid}
  {onOpenDelay}
  {onCloseDelay}
  {allowClickOutside}
  {allowCloseOnNavigation}
  class="max-h-full {status === 'open' && isSheetLayout
    ? isTopOfStack
      ? `translate-y-[var(--sheet-y)] scale-100`
      : `translate-y-[calc(-3%*var(--stack-depth)+var(--sheet-y,0px))] scale-[calc(100%-5%*var(--stack-depth,0))]`
    : ``} {dialogClass} m-auto max-w-full transition-all will-change-[transform,opacity] {isSheetLayout
    ? 'h-full w-full origin-top opacity-[var(--sheet-opacity)]'
    : ''} {isSheetLayout ? sheetTransitionClasses[status] : dialogTransitionClasses[status]} {dialogClass.match(/\bw-/g)
    ? ''
    : `${width} max-w-[calc(100svw-32px)]`}"
  style="--stack-depth: {stackDepth};"
  on:open={() => {
    addToStack();
    dispatch('open');
    setTimeout(() => {
      scrollerEl?.scrollTo(0, 0);
    });
  }}
  on:closing={() => {
    setTimeout(() => {
      removeFromStack();
    }, onCloseDelay / 2);
    dispatch('closing');
  }}
  on:closed={() => {
    focusReady = focusOnOpen;
    dispatch('closed');
  }}
  on:opened={() => {
    focusReady = true;
    dispatch('opened');
  }}
>
  <section
    inert={!focusReady}
    class="{isSheetLayout ? 'sheet shadow-sheet' : 'sm:shadow-modal m-10 rounded-lg'} {isSheetLayout && fullHeightSheet
      ? 'h-full'
      : ''} border-ondark-subtle bg-ondark-base flex flex-col border p-3 pt-5 text-center sm:max-h-[calc(100svh-80px)] sm:rounded-lg sm:p-5 md:p-6 {className}"
    {style}
  >
    {#if allowClickOutside}
      <Grabber />
    {/if}

    {#if $$slots.title || title || subtitle || $$slots.icons || closeButton}
      <header class="z-1 flex items-start gap-x-4">
        {#if backIcon}
          <IconButton
            variant="secondary"
            icon={faChevronLeft}
            size="sm"
            class="text-action-ondark-base"
            on:click={() => {
              dialog?.closeModal();
            }}
          />
        {/if}

        <slot name="title">
          <section class="flex flex-col gap-1 only:mr-auto only:pl-2 sm:only:mx-auto sm:only:pl-0">
            <div class="flex items-center gap-4">
              {#if titleIcon}
                <IconWrapper icon={titleIcon} size="lg" class="text-ondark-primary" />
              {/if}
              <h3
                class="{titleSizeClasses[titleSize]} text-ondark-primary flex items-center sm:text-balance {!subtitle
                  ? 'min-h-8'
                  : ''}"
              >
                {title}
              </h3>
            </div>
            {#if subtitle}
              <p class="text-ondark-secondary text-body-2xs mr-auto">
                {subtitle}
              </p>
            {/if}
          </section>
        </slot>

        {#if $$slots.icons || (!isSheetLayout && closeButton) || (isSheetLayout && mobileCloseButton)}
          <section class="actions -mt-1 ml-auto flex items-center justify-end gap-2">
            {#if $$slots.icons}
              <slot name="icons" />
            {/if}

            {#if (!isSheetLayout && closeButton) || (isSheetLayout && mobileCloseButton)}
              <IconButton
                class={closeButtonClass}
                icon={faXmark}
                variant="ghost"
                on:click={() => dialog?.closeModal()}
              />
            {/if}
          </section>
        {/if}
      </header>
    {/if}

    <!-- Main -->
    <div
      bind:this={scrollerEl}
      class="scrollbar-hide sm:scrollbar-thin text-ondark-secondary flex-1 overflow-y-auto {scrollerClass}"
      class:mt-4={$$slots.title || title || $$slots.icons || closeButton}
      use:scrollerSheetPanel
    >
      <slot />
    </div>

    {#if primaryAction || secondaryAction || $$slots.footer}
      <footer class="mt-4 flex items-center gap-4 {justifyButtons}">
        {#if $$slots.footer}
          <slot name="footer" />
        {/if}
        {#each [secondaryAction, primaryAction] as buttonAction, index}
          {#if buttonAction}
            <Button
              variant={buttonAction?.variant ? buttonAction?.variant : index === 1 ? 'primary' : 'secondary'}
              href={buttonAction?.href}
              size={buttonAction?.size || 'md'}
              on:click={() => {
                buttonAction?.action?.();

                // Prevent impacting older dialogs
                if (closeOnActionClick) {
                  dialog?.closeModal();
                }
              }}
              width={isSheetLayout ? 'full' : buttonAction?.width}
              loading={buttonAction?.loading}
              disabled={buttonAction?.disabled}
              use={buttonAction?.use}
            >
              {buttonAction.label}
            </Button>
          {/if}
        {/each}
      </footer>
    {/if}
  </section>
</Skeleton>
